import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  compactFormatFor,
  pledgedForReq,
  totalPeopleCommitted,
  totalPeopleNeeded,
  uniqueHelpersCount,
  attendanceCount,
  openRequirements,
} from "../lib/domain";

// Micro momentum line — surfaces change over existence.
// "1 wheelbarrow left." beats "Community Garden Day" for a returning user.
function momentumBrief(project, pledges) {
  const open = openRequirements(project, pledges);
  if (!open.length) return "Everything is here.";
  const gaps = open
    .map((r) => ({ req: r, gap: Math.max(0, r.needed - pledgedForReq(r.id, pledges)) }))
    .filter((x) => x.gap > 0);
  if (!gaps.length) return "Everything is here.";
  if (gaps.length === 1 && gaps[0].gap === 1) {
    const r = gaps[0].req;
    if (r.kind === "people") return `Waiting on one final ${r.label.toLowerCase()}.`;
    return `One ${r.label.toLowerCase()} left.`;
  }
  const total = gaps.reduce((s, x) => s + x.gap, 0);
  return `${total} ${total === 1 ? "thing" : "things"} still needed.`;
}

// Derive the compact card format from a project's semantic effective status.
// The mapping now lives in `lib/domain.js` (`compactFormatFor`) so tests
// and non-React callers can reason about card selection without dragging
// in the component tree.

/*
 * Compact / medium card for projects on the Profile and inside category
 * shelves. Three formats, all derived internally from `effectiveStatus`:
 *   - "gathering"  — neutral compact tile for projects still gathering
 *   - "happening"  — medium card for It's Happening projects
 *   - "story"      — small archival tile for Completed projects
 *
 * The full editorial ProjectCard is reserved for gathering projects that
 * appear inside category shelves where the fuller narrative is warranted.
 * Compact + story cards link to Project Detail for the full narrative.
 *
 * Status determination is intentionally decoupled from card-format
 * selection: callers describe *which project* to render, and the card
 * decides *how* to render it based on the project's actual state.
 */
export default function ProjectCardCompact({ project, pledges, people, onClick, context = "library" }) {
  const nav = useNavigate();
  const format = compactFormatFor(project, pledges);
  const helpers = uniqueHelpersCount(project.id, pledges);
  const showedUp = attendanceCount(project, pledges);

  const peopleCommitted = totalPeopleCommitted(project, pledges);
  const peopleNeeded = totalPeopleNeeded(project);
  const thingReqs = project.requirements.filter((r) => r.kind === "thing");
  const thingsNeeded = thingReqs.reduce((s, r) => s + r.needed, 0);
  const thingsPledged = thingReqs.reduce(
    (s, r) => s + Math.min(r.needed, pledgedForReq(r.id, pledges)),
    0
  );

  const testId = `project-card-${project.id}`;
  const goDetail = onClick || (() => nav(`/project/${project.id}`));

  if (format === "story") {
    return (
      <button
        className="c-mini c-mini-story"
        onClick={goDetail}
        data-testid={testId}
        data-compact-format="story"
      >
        <div className="c-mini-emoji">{project.emoji}</div>
        <div className="c-mini-body">
          <div className="c-mini-title-row">
            <div className="c-mini-title">{project.title}</div>
            <span className="c-mini-badge c-mini-badge-story">✿</span>
          </div>
          <div className="c-mini-meta">
            {project.when}{showedUp ? ` · ${showedUp} showed up` : ""}
          </div>
          {project.story?.what ? (
            <div className="c-mini-quote">"{project.story.what}"</div>
          ) : null}
        </div>
        <ChevronRight size={16} className="c-mini-chevron" />
      </button>
    );
  }

  if (format === "gathering") {
    // Profile answers "what am I part of?" — Gathering there belongs to
    // the same living-green family as It's Happening (quieter, sprout).
    // Library answers "what's out there?" — Gathering keeps its warm
    // cream/ochre editorial identity. Context selects the palette; the
    // semantic format is still derived from effectiveStatus above.
    const gatheringClass =
      context === "profile"
        ? "c-mini c-mini-gathering c-mini-gathering-living"
        : "c-mini c-mini-gathering";
    const badgeClass =
      context === "profile"
        ? "c-mini-badge c-mini-badge-gathering c-mini-badge-gathering-living"
        : "c-mini-badge c-mini-badge-gathering";
    return (
      <button
        className={gatheringClass}
        onClick={goDetail}
        data-testid={testId}
        data-compact-format="gathering"
        data-compact-context={context}
      >
        <div className="c-mini-gathering-accent" />
        <div className="c-mini-gathering-inner">
          <div className="c-mini-title-row">
            <div className="c-mini-emoji">{project.emoji}</div>
            <div className="c-mini-title">{project.title}</div>
            <span className={badgeClass}>🌱</span>
          </div>
          <div className="c-mini-meta">
            Gathering{project.when ? ` · ${project.when}` : ""}
          </div>
          <div className="c-mini-stat-row">
            <span className="c-mini-stat">
              <strong>{peopleCommitted}</strong> of {peopleNeeded} people
            </span>
            {thingsNeeded > 0 ? (
              <span className="c-mini-stat">
                <strong>{thingsPledged}</strong> of {thingsNeeded} things
              </span>
            ) : null}
          </div>
        </div>
        <ChevronRight size={18} className="c-mini-chevron" />
      </button>
    );
  }

  // happening variant — medium card
  const change = momentumBrief(project, pledges);
  return (
    <button
      className="c-mini c-mini-happening"
      onClick={goDetail}
      data-testid={testId}
      data-compact-format="happening"
    >
      <div className="c-mini-happening-accent" />
      <div className="c-mini-happening-inner">
        <div className="c-mini-title-row">
          <div className="c-mini-emoji">{project.emoji}</div>
          <div className="c-mini-title">{project.title}</div>
          <span className="c-mini-badge c-mini-badge-happening">🌿</span>
        </div>
        <div className="c-mini-meta">
          It's happening · {project.when}
        </div>
        <div className="c-mini-stat-row">
          <span className="c-mini-stat">
            <strong>{peopleCommitted}</strong> of {peopleNeeded} people
          </span>
          {thingsNeeded > 0 ? (
            <span className="c-mini-stat">
              <strong>{thingsPledged}</strong> of {thingsNeeded} things
            </span>
          ) : null}
        </div>
        {change ? <div className="c-mini-change">{change}</div> : null}
      </div>
      <ChevronRight size={18} className="c-mini-chevron" />
    </button>
  );
}
